import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import Header from "@/components/Header";
import Stepper from "@/components/wizard/Stepper";
import OptionCard from "@/components/wizard/OptionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getConfig, createPreview, captureLead, fmtMoney, fmtInvestment, crmGetProspect, crmCompleteAssessment } from "@/lib/api";
import { getToken } from "@/lib/auth";
import { PRICING_DISCLAIMER } from "@/lib/constants";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import CountUp from "@/components/CountUp";
import AdvisorPanel from "@/components/advisor/AdvisorPanel";
import AssessmentMemory from "@/components/advisor/AssessmentMemory";
import EmergingInsight from "@/components/advisor/EmergingInsight";
import StepTransition from "@/components/advisor/StepTransition";
import FinalAnalysis from "@/components/advisor/FinalAnalysis";
import {
  FINAL_STAGES,
  getAdvisorMessage,
  getTransition,
  getEmergingInsight,
  getFactors,
  getPrelimMessage,
  METHOD_NOTE,
} from "@/lib/advisor";
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Lock,
  CheckCircle2,
  Stethoscope,
  Cpu,
  Briefcase,
  ShoppingCart,
  Home,
  Landmark,
  Megaphone,
  Building2,
  Gauge,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const INDUSTRY_ICONS = {
  healthcare: Stethoscope,
  technology: Cpu,
  professional_services: Briefcase,
  ecommerce_retail: ShoppingCart,
  real_estate: Home,
  finance: Landmark,
  marketing_agency: Megaphone,
  other: Building2,
};

const STEPS = [
  { key: "industry", label: "Industry" },
  { key: "company", label: "Company" },
  { key: "workload", label: "Workload" },
  { key: "pains", label: "Pain Points" },
  { key: "goals", label: "Goals" },
];

export default function Assessment() {
  const navigate = useNavigate();
  const { prospectId } = useParams();
  const crmMode = !!prospectId;
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState(0);
  const [view, setView] = useState("wizard"); // wizard | preview | lead
  const [busy, setBusy] = useState(false);
  const [answers, setAnswers] = useState({
    industry: "",
    company_size: "",
    company_name: "",
    current_team_size: "",
    workload: "",
    pain_points: [],
    growth_goal: "",
    timeline: "",
  });
  const [preview, setPreview] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);
  const [lead, setLead] = useState({ name: "", company: "", email: "", phone: "" });
  const [leadErr, setLeadErr] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState(""); // bots fill this; humans never see it
  const leadStartRef = useRef(null);
  const reduced = useReducedMotion();
  const [transitioning, setTransitioning] = useState(false);
  const [transitionMsg, setTransitionMsg] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisDone, setAnalysisDone] = useState(false);

  // TeamBuilder visit attribution: capture the opaque ?tb= token (set by the
  // tracked-link redirect) and persist it for the whole assessment session so
  // both the preview (Started) and lead (Submitted) calls carry attribution.
  const [tbToken, setTbToken] = useState(null);
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const fromUrl = params.get("tb");
      if (fromUrl) {
        sessionStorage.setItem("tb_token", fromUrl);
        setTbToken(fromUrl);
        // Clean the token out of the visible URL (keep the session copy).
        params.delete("tb");
        const qs = params.toString();
        window.history.replaceState({}, "", window.location.pathname + (qs ? `?${qs}` : ""));
      } else {
        const stored = sessionStorage.getItem("tb_token");
        if (stored) setTbToken(stored);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    getConfig()
      .then(setConfig)
      .catch(() => toast.error("Failed to load assessment. Please refresh."));
  }, []);

  // CRM entry point: prefill known values from the originating prospect.
  useEffect(() => {
    if (!crmMode || !config) return;
    const token = getToken();
    if (!token) { navigate("/admin?returnTo=/crm"); return; }
    crmGetProspect(token, prospectId)
      .then(({ prospect: p }) => {
        const industryMatch = (config.industries || []).find((i) => {
          const a = (i.label || "").toLowerCase();
          const b = (p.industry || "").toLowerCase();
          return b && (a.includes(b) || b.includes(a));
        });
        const ec = p.employee_count;
        const sizeMatch = ec != null
          ? (config.company_sizes || []).find((s) => {
              const nums = (s.label || "").match(/\d+/g);
              if (!nums) return false;
              const lo = parseInt(nums[0], 10);
              const hi = nums[1] ? parseInt(nums[1], 10) : Infinity;
              return ec >= lo && ec <= hi;
            })
          : null;
        setAnswers((a) => ({
          ...a,
          company_name: p.company_name || "",
          industry: industryMatch ? industryMatch.key : a.industry,
          company_size: sizeMatch ? sizeMatch.key : a.company_size,
        }));
        setLead({
          name: p.name || "",
          company: p.company_name || "",
          email: p.email || "",
          phone: p.corporate_phone || "",
        });
      })
      .catch(() => toast.error("Could not load prospect for the assessment."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crmMode, config, prospectId]);

  const set = (patch) => setAnswers((a) => ({ ...a, ...patch }));
  const togglePain = (key) =>
    setAnswers((a) => ({
      ...a,
      pain_points: a.pain_points.includes(key)
        ? a.pain_points.filter((p) => p !== key)
        : [...a.pain_points, key],
    }));

  const relevantPains = config
    ? config.pain_points.filter(
        (p) => !p.industries || p.industries.includes(answers.industry)
      )
    : [];

  const canProceed = () => {
    switch (STEPS[step].key) {
      case "industry":
        return !!answers.industry;
      case "company":
        return !!answers.company_size;
      case "workload":
        return !!answers.workload;
      case "pains":
        return answers.pain_points.length >= 1;
      case "goals":
        return !!answers.growth_goal;
      default:
        return true;
    }
  };

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const next = async () => {
    if (!canProceed()) {
      toast.error("Please make a selection to continue.");
      return;
    }
    const stepKey = STEPS[step].key;

    if (step < STEPS.length - 1) {
      // Short contextual analysis transition between steps
      setTransitionMsg(getTransition(stepKey, answers));
      setTransitioning(true);
      await sleep(reduced ? 150 : 1200);
      setTransitioning(false);
      setStep((s) => s + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    // Final step -> full analysis sequence tied to real backend work
    setAnalysisDone(false);
    setAnalyzing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
    const started = Date.now();
    try {
      const data = await createPreview(answers, crmMode ? undefined : tbToken); // real processing
      setPreview(data.preview);
      setSubmissionId(data.submission_id);
      setLead((l) => ({ ...l, company: answers.company_name || l.company || "" }));
      // Honest minimum perceived duration
      const minMs = reduced ? 500 : 4000;
      const elapsed = Date.now() - started;
      if (elapsed < minMs) await sleep(minMs - elapsed);
      setAnalysisDone(true);
      await sleep(reduced ? 100 : 700); // let final stages complete visually
      setAnalyzing(false);
      // CRM entry point: finalize against the prospect + create/refresh the Deal,
      // then return to the Prospect Workspace (same engine, no lead-capture form).
      if (crmMode) {
        try {
          const token = getToken();
          await crmCompleteAssessment(token, prospectId, data.submission_id);
          toast.success("Blueprint generated and attached to the prospect.");
          navigate(`/crm?prospect=${prospectId}`);
          return;
        } catch (e) {
          toast.error("Assessment finished but linking to the prospect failed.");
          navigate(`/crm?prospect=${prospectId}`);
          return;
        }
      }
      setView("preview");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      setAnalyzing(false);
      toast.error("Could not generate your preview. Please try again.");
    }
  };

  const back = () => {
    if (view === "lead") {
      setView("preview");
      return;
    }
    if (step > 0) setStep((s) => s - 1);
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadErr("");
    if (!lead.name || !lead.company || !lead.email || !lead.phone) {
      setLeadErr("Please complete all fields.");
      return;
    }
    if (!consent) {
      setLeadErr("Please accept the Privacy Policy to continue.");
      return;
    }
    setBusy(true);
    try {
      const elapsed = leadStartRef.current ? Date.now() - leadStartRef.current : null;
      const res = await captureLead({
        submission_id: submissionId,
        ...lead,
        consent,
        website: honeypot,
        elapsed_ms: elapsed,
      }, crmMode ? undefined : tbToken);
      toast.success("Your blueprint is ready!");
      try { sessionStorage.removeItem("tb_token"); } catch { /* ignore */ }
      const tok = res?.download_token ? `?pdf_token=${encodeURIComponent(res.download_token)}` : "";
      navigate(`/blueprint/${submissionId}${tok}`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setLeadErr(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!config) {
    return (
      <div className="min-h-screen rd-bg text-white">
        <Header />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="animate-spin text-[#1E6BFF]" size={28} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen rd-bg text-white">
      <Header />
      {view === "wizard" && !analyzing && <Stepper steps={STEPS} current={step} />}

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {view === "wizard" && analyzing && (
          <FinalAnalysis stages={FINAL_STAGES} done={analysisDone} reduced={reduced} />
        )}

        {view === "wizard" && !analyzing && (
          <>
            {step >= 1 && <AssessmentMemory factors={getFactors(answers, config)} />}
            <AdvisorPanel message={getAdvisorMessage(STEPS[step].key, answers)} reduced={reduced} />
            {transitioning ? (
              <StepTransition message={transitionMsg} reduced={reduced} />
            ) : (
          <AnimatePresence mode="wait">
            <motion.div
              key={STEPS[step].key}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.28 }}
            >
              {/* STEP: INDUSTRY */}
              {STEPS[step].key === "industry" && (
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold">What industry are you in?</h2>
                  <p className="text-white/60 mt-2">This tailors the roles we recommend.</p>
                  <div className="grid sm:grid-cols-2 gap-3 mt-8">
                    {config.industries.map((ind) => (
                      <OptionCard
                        key={ind.key}
                        testId={`assessment-industry-option-${ind.key}`}
                        icon={INDUSTRY_ICONS[ind.key] || Building2}
                        title={ind.label}
                        selected={answers.industry === ind.key}
                        onClick={() =>
                          set({ industry: ind.key, pain_points: [] })
                        }
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* STEP: COMPANY */}
              {STEPS[step].key === "company" && (
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold">Tell us about your company</h2>
                  <p className="text-white/60 mt-2">A few basics to right-size your plan.</p>
                  <div className="mt-8 space-y-6">
                    <div>
                      <Label className="text-white/80 mb-2 block">Company name (optional)</Label>
                      <Input
                        data-testid="assessment-company-name-input"
                        value={answers.company_name}
                        onChange={(e) => set({ company_name: e.target.value })}
                        placeholder="Acme Inc."
                        className="bg-[#0F1424] border-white/10 h-11 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white/80 mb-3 block">
                        How many employees do you have?
                      </Label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {config.company_sizes.map((cs) => (
                          <OptionCard
                            key={cs}
                            testId={`assessment-company-size-${cs}`}
                            title={`${cs} employees`}
                            selected={answers.company_size === cs}
                            onClick={() => set({ company_size: cs })}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-white/80 mb-2 block">
                        Current support/admin team size (optional)
                      </Label>
                      <Select
                        value={answers.current_team_size}
                        onValueChange={(v) => set({ current_team_size: v })}
                      >
                        <SelectTrigger
                          data-testid="assessment-current-team-select"
                          className="bg-[#0F1424] border-white/10 h-11 text-white"
                        >
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          {["None", "1-2", "3-5", "6-10", "10+"].map((o) => (
                            <SelectItem key={o} value={o}>
                              {o}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              )}

              {/* STEP: WORKLOAD */}
              {STEPS[step].key === "workload" && (
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold">
                    How much repetitive work piles up weekly?
                  </h2>
                  <p className="text-white/60 mt-2">
                    Think admin, support, follow-ups, and manual tasks.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 mt-8">
                    {config.workloads.map((w) => (
                      <OptionCard
                        key={w.key}
                        testId={`assessment-workload-${w.key}`}
                        icon={Gauge}
                        title={w.label}
                        selected={answers.workload === w.key}
                        onClick={() => set({ workload: w.key })}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* STEP: PAIN POINTS */}
              {STEPS[step].key === "pains" && (
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold">
                    Where does it hurt most?
                  </h2>
                  <p className="text-white/60 mt-2">
                    Select all that apply &mdash; we&apos;ll target these first.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-3 mt-8">
                    {relevantPains.map((p) => (
                      <OptionCard
                        key={p.key}
                        testId={`assessment-pain-${p.key}`}
                        title={p.label}
                        multi
                        selected={answers.pain_points.includes(p.key)}
                        onClick={() => togglePain(p.key)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* STEP: GOALS */}
              {STEPS[step].key === "goals" && (
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold">
                    What&apos;s your growth goal?
                  </h2>
                  <p className="text-white/60 mt-2">This shapes how we scale your team.</p>
                  <div className="grid sm:grid-cols-3 gap-3 mt-8">
                    {config.growth_goals.map((g) => (
                      <OptionCard
                        key={g.key}
                        testId={`assessment-growth-${g.key}`}
                        icon={TrendingUp}
                        title={g.label}
                        selected={answers.growth_goal === g.key}
                        onClick={() => set({ growth_goal: g.key })}
                      />
                    ))}
                  </div>
                  <div className="mt-6">
                    <Label className="text-white/80 mb-2 block">
                      When do you want to start? (optional)
                    </Label>
                    <Select
                      value={answers.timeline}
                      onValueChange={(v) => set({ timeline: v })}
                    >
                      <SelectTrigger
                        data-testid="assessment-timeline-select"
                        className="bg-[#0F1424] border-white/10 h-11 text-white"
                      >
                        <SelectValue placeholder="Select timeline" />
                      </SelectTrigger>
                      <SelectContent>
                        {config.timelines.map((t) => (
                          <SelectItem key={t.key} value={t.key}>
                            {t.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>
            )}
            {!transitioning && (
              <EmergingInsight insight={getEmergingInsight(STEPS[step].key, answers)} reduced={reduced} />
            )}
          </>
        )}

        {/* PREVIEW */}
        {view === "preview" && preview && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="preview-results"
          >
            <div className="inline-flex items-center gap-2 rounded-full border border-[#1E6BFF]/30 bg-[#1E6BFF]/10 px-3 py-1.5 text-xs text-[#66B2FF] mb-4">
              <Sparkles size={14} /> Preliminary result &mdash; {preview.industry_label}
            </div>
            <AdvisorPanel message={getPrelimMessage(preview)} reduced={reduced} />
            <h2 className="text-2xl sm:text-3xl font-bold">
              Your workforce blueprint is taking shape
            </h2>
            <p className="text-white/60 mt-2">
              Here&apos;s a preview. Unlock the full blueprint with your recommended team,
              responsibilities, and implementation roadmap.
            </p>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
                <div className="text-3xl font-bold text-white tabular-nums">
                  <CountUp value={preview.total_headcount} reduced={reduced} />
                </div>
                <div className="text-white/55 text-sm mt-1">Recommended team seats</div>
              </div>
              {config?.show_pricing ? (
                <>
                  <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
                    <div className="text-xl font-bold text-white tabular-nums">
                      {fmtInvestment(preview.investment_low, preview.investment_high)}
                    </div>
                    <div className="text-white/55 text-sm mt-1">Estimated Monthly Investment</div>
                  </div>
                  <div className="rounded-xl border border-[#1E6BFF]/25 p-5" style={{ backgroundColor: "#101a33" }}>
                    <div className="text-xl font-bold text-[#66B2FF] tabular-nums">
                      {fmtMoney(preview.savings_annual)}
                    </div>
                    <div className="text-white/55 text-sm mt-1">Estimated Annual Savings</div>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-xl border border-white/10 p-5" style={{ backgroundColor: "#0F1424" }}>
                    <div className="text-xl font-bold text-white">2&ndash;4 weeks</div>
                    <div className="text-white/55 text-sm mt-1">Estimated implementation</div>
                  </div>
                  <div className="rounded-xl border border-[#1E6BFF]/25 p-5" style={{ backgroundColor: "#101a33" }}>
                    <div className="text-xl font-bold text-[#66B2FF]">{preview.industry_label}</div>
                    <div className="text-white/55 text-sm mt-1">Operational focus</div>
                  </div>
                </>
              )}
            </div>

            {config?.show_pricing && (
              <p className="text-white/40 text-xs mt-3 leading-relaxed" data-testid="preview-pricing-disclaimer">
                {PRICING_DISCLAIMER}
              </p>
            )}

            <div className="rounded-xl border border-white/10 p-5 mt-4" style={{ backgroundColor: "#0F1424" }}>
              <h3 className="text-white font-semibold mb-3">Top recommended roles</h3>
              <div className="space-y-2">
                {preview.top_roles.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <span className="text-white/85 text-sm">{r.name}</span>
                    <span className="text-white/50 text-xs">
                      {r.quantity} seat{r.quantity > 1 ? "s" : ""} &middot; {r.category}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Locked blueprint teaser */}
            <p className="text-white/40 text-xs mt-6 leading-relaxed" data-testid="method-note">
              {METHOD_NOTE}
            </p>
            <div className="relative rounded-xl border border-white/10 mt-4 overflow-hidden" style={{ backgroundColor: "#0F1424" }}>
              <div className="p-6 blur-[3px] select-none pointer-events-none">
                <p className="text-white/70 text-sm leading-relaxed">
                  Your personalized executive narrative, full role breakdown with
                  why-recommended signals, team summary, and a 4-phase
                  implementation roadmap are ready inside your complete blueprint...
                </p>
              </div>
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ background: "linear-gradient(180deg, rgba(15,20,36,0.4), rgba(15,20,36,0.92))" }}>
                <span className="rounded-full bg-[#1E6BFF] p-3 text-white">
                  <Lock size={20} />
                </span>
                <Button
                  data-testid="preview-unlock-blueprint-button"
                  onClick={() => {
                    leadStartRef.current = Date.now();
                    setView("lead");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11 px-6"
                >
                  Unlock my full blueprint <ArrowRight size={18} className="ml-1" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}

        {/* LEAD GATE */}
        {view === "lead" && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid lg:grid-cols-2 gap-8"
            data-testid="lead-capture-form"
          >
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold">
                Unlock your complete blueprint
              </h2>
              <p className="text-white/60 mt-2">
                Enter your details and we&apos;ll reveal your full personalized plan
                instantly.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  "Full role breakdown with responsibilities & why-recommended signals",
                  "AI-generated executive summary for your business",
                  "Team summary with implementation timeline & operational benefits",
                  "4-phase implementation roadmap",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-2.5 text-white/75 text-sm">
                    <CheckCircle2 size={18} className="text-[#66B2FF] shrink-0 mt-0.5" />
                    {t}
                  </li>
                ))}
              </ul>
              <p className="text-white/40 text-xs mt-6">
                We respect your privacy. Your information is used only to prepare and
                discuss your workforce plan.
              </p>
            </div>

            <form
              onSubmit={submitLead}
              className="rounded-xl border border-white/10 p-6 space-y-4"
              style={{ backgroundColor: "#0F1424" }}
            >
              <div>
                <Label className="text-white/80 mb-2 block">Full name</Label>
                <Input
                  data-testid="lead-name-input"
                  value={lead.name}
                  onChange={(e) => setLead({ ...lead, name: e.target.value })}
                  placeholder="Jane Smith"
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Company</Label>
                <Input
                  data-testid="lead-company-input"
                  value={lead.company}
                  onChange={(e) => setLead({ ...lead, company: e.target.value })}
                  placeholder="Acme Inc."
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Business or personal email</Label>
                <Input
                  data-testid="lead-email-input"
                  type="email"
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="you@company.com"
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Phone</Label>
                <Input
                  data-testid="lead-phone-input"
                  value={lead.phone}
                  onChange={(e) => setLead({ ...lead, phone: e.target.value })}
                  placeholder="+1 (555) 123-4567"
                  className="bg-[#0A0F1E] border-white/10 h-11 text-white"
                />
              </div>

              {/* Honeypot: hidden from users, catches bots */}
              <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
                <label>Website</label>
                <input
                  tabIndex={-1}
                  autoComplete="off"
                  data-testid="lead-website-honeypot"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                />
              </div>

              {/* Consent */}
              <div className="flex items-start gap-3 pt-1">
                <Checkbox
                  id="lead-consent"
                  data-testid="lead-consent-checkbox"
                  checked={consent}
                  onCheckedChange={(v) => setConsent(!!v)}
                  className="mt-0.5 border-white/30 data-[state=checked]:bg-[#1E6BFF] data-[state=checked]:border-[#1E6BFF]"
                />
                <label htmlFor="lead-consent" className="text-white/65 text-xs leading-relaxed cursor-pointer">
                  I agree to Rendeavored's{" "}
                  <a
                    href={config.privacy_url || "https://rendeavored.com/privacy"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#66B2FF] underline"
                    data-testid="lead-privacy-link"
                  >
                    Privacy Policy
                  </a>{" "}
                  and to being contacted about my workforce plan.
                </label>
              </div>
              {leadErr && (
                <p className="text-[#FF5C7A] text-sm" data-testid="lead-error" aria-live="polite">
                  {leadErr}
                </p>
              )}
              <Button
                type="submit"
                disabled={busy}
                data-testid="lead-capture-submit-button"
                className="w-full bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11"
              >
                {busy ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={18} /> Building your blueprint...
                  </>
                ) : (
                  <>Reveal my blueprint <ArrowRight size={18} className="ml-1" /></>
                )}
              </Button>
            </form>
          </motion.div>
        )}

        {/* NAV BUTTONS (wizard only) */}
        {view === "wizard" && !analyzing && !transitioning && (
          <div className="flex items-center justify-between mt-10">
            <Button
              variant="ghost"
              onClick={back}
              disabled={step === 0}
              data-testid="assessment-back-button"
              className="text-white/70 hover:text-white hover:bg-white/5 disabled:opacity-30"
            >
              <ArrowLeft size={18} className="mr-1" /> Back
            </Button>
            <Button
              onClick={next}
              disabled={busy}
              data-testid="assessment-next-button"
              className="bg-[#1E6BFF] hover:bg-[#3B82F6] text-white rounded-xl h-11 px-6"
            >
              {busy ? (
                <Loader2 className="animate-spin" size={18} />
              ) : step === STEPS.length - 1 ? (
                <>See my preview <ArrowRight size={18} className="ml-1" /></>
              ) : (
                <>Continue <ArrowRight size={18} className="ml-1" /></>
              )}
            </Button>
          </div>
        )}

        {view === "preview" && (
          <div className="mt-8">
            <Button
              variant="ghost"
              onClick={() => setView("wizard")}
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="preview-back-button"
            >
              <ArrowLeft size={18} className="mr-1" /> Edit my answers
            </Button>
          </div>
        )}

        {view === "lead" && (
          <div className="mt-6">
            <Button
              variant="ghost"
              onClick={back}
              className="text-white/60 hover:text-white hover:bg-white/5"
              data-testid="lead-back-button"
            >
              <ArrowLeft size={18} className="mr-1" /> Back to preview
            </Button>
          </div>
        )}
      </main>
    </div>
  );
}
